(() => {
  'use strict';

  if (window.__ndrHeaderRecIntegrityV2) return;
  window.__ndrHeaderRecIntegrityV2 = true;

  const productCache = new Map();
  const pendingCards = new WeakMap();
  const replayClicks = new WeakSet();
  let observedHeader = null;
  let headerObserver = null;

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  const optionNames = (product) => (Array.isArray(product?.options) ? product.options : [])
    .map((option) => typeof option === 'string' ? option : (option?.name || option?.title || ''));

  const variantOptions = (variant) => {
    if (Array.isArray(variant?.options)) return variant.options.map((value) => String(value ?? ''));
    return [variant?.option1, variant?.option2, variant?.option3]
      .filter((value) => value != null)
      .map((value) => String(value));
  };

  const sizeIndexFor = (product) => optionNames(product).findIndex((name) => {
    const key = normalize(name);
    return key.includes('talla') || key === 'size' || key.includes('tamano') || key.includes('talle');
  });

  const productUrlForCard = (card) => {
    const link = card?.querySelector('a[href*="/products/"]');
    if (!link?.href) return null;

    try {
      const url = new URL(link.href, window.location.origin);
      url.search = '';
      url.hash = '';
      url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      return url.toString();
    } catch (error) {
      return null;
    }
  };

  const fetchProduct = (card) => {
    const url = productUrlForCard(card);
    if (!url) return Promise.resolve(null);
    if (productCache.has(url)) return productCache.get(url);

    const request = fetch(url, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      cache: 'force-cache'
    }).then((response) => {
      if (!response.ok) throw new Error(`Product JSON ${response.status}`);
      return response.json();
    }).catch((error) => {
      productCache.delete(url);
      console.warn('NDR recommendation integrity: no fue posible validar el producto.', error);
      return null;
    });

    productCache.set(url, request);
    return request;
  };

  const buildSafeOptions = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const anchor = variants.find((variant) => variant?.available);
    if (!anchor) return [];

    const sizeIndex = sizeIndexFor(product);
    if (sizeIndex < 0) return [{ id: anchor.id, title: 'Única talla' }];

    const anchorOptions = variantOptions(anchor);
    const seen = new Set();
    const safe = [];

    variants.forEach((variant) => {
      if (!variant?.available) return;
      const options = variantOptions(variant);
      const size = String(options[sizeIndex] || '').trim();
      if (!size || seen.has(normalize(size))) return;

      const matchesAnchor = options.every((value, index) => {
        if (index === sizeIndex) return true;
        return normalize(value) === normalize(anchorOptions[index]);
      });

      if (!matchesAnchor) return;
      seen.add(normalize(size));
      safe.push({ id: variant.id, title: size });
    });

    return safe;
  };

  const applySafeOptions = (card, safeOptions) => {
    const controls = card?.querySelector('[data-eme-rec-variants]');
    if (!controls || !Array.isArray(safeOptions) || !safeOptions.length) return false;

    const byTitle = new Map(safeOptions.map((option) => [normalize(option.title), option]));

    if (controls.dataset.emeVariantsReady !== 'true') {
      controls.dataset.emeVariantOptions = JSON.stringify(safeOptions);
    }

    controls.querySelectorAll('[data-eme-rec-variant-choice]').forEach((button) => {
      const title = normalize(button.dataset.variantTitle || button.textContent);
      const match = byTitle.get(title);

      if (match) {
        button.dataset.variantId = String(match.id);
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      } else {
        button.dataset.variantId = '';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      }
    });

    card.dataset.ndrRecIntegrity = 'ready';
    return true;
  };

  const ensureCard = (card) => {
    if (!card) return Promise.resolve(false);
    if (card.dataset.ndrRecIntegrity === 'ready') return Promise.resolve(true);
    if (pendingCards.has(card)) return pendingCards.get(card);

    const request = (async () => {
      const product = await fetchProduct(card);
      if (!product) {
        card.dataset.ndrRecIntegrity = 'failed';
        return false;
      }

      const safeOptions = buildSafeOptions(product);
      const applied = applySafeOptions(card, safeOptions);
      if (!applied) card.dataset.ndrRecIntegrity = 'failed';
      return applied;
    })().finally(() => pendingCards.delete(card));

    pendingCards.set(card, request);
    return request;
  };

  const scan = (scope) => {
    if (!scope) return;
    const cards = [];
    if (scope instanceof Element && scope.matches('[data-eme-recommendation-card]')) cards.push(scope);
    scope.querySelectorAll?.('[data-eme-recommendation-card]').forEach((card) => cards.push(card));
    cards.forEach((card) => void ensureCard(card));
  };

  const ensureHeaderObserver = () => {
    const header = document.querySelector('[data-eme-header]');
    if (!header) return null;
    if (header === observedHeader && headerObserver) return header;

    headerObserver?.disconnect();
    observedHeader = header;
    headerObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) scan(node);
        }
      }
    });
    headerObserver.observe(header, { childList: true, subtree: true });
    scan(header);
    return header;
  };

  document.addEventListener('pointerover', (event) => {
    const card = event.target.closest?.('[data-eme-header] [data-eme-recommendation-card]');
    if (card) void ensureCard(card);
  }, { capture: true, passive: true });

  document.addEventListener('focusin', (event) => {
    const card = event.target.closest?.('[data-eme-header] [data-eme-recommendation-card]');
    if (card) void ensureCard(card);
  }, true);

  document.addEventListener('click', async (event) => {
    const control = event.target.closest?.('[data-eme-rec-toggle], [data-eme-rec-variant-choice]');
    if (!control || !control.closest('[data-eme-header]')) return;
    if (replayClicks.has(control)) {
      replayClicks.delete(control);
      return;
    }

    const card = control.closest('[data-eme-recommendation-card]');
    if (!card || card.dataset.ndrRecIntegrity === 'ready') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    await ensureCard(card);
    if (!control.isConnected) return;

    replayClicks.add(control);
    control.click();
  }, true);

  const rescanHeader = () => {
    const header = ensureHeaderObserver();
    if (header) scan(header);
  };

  const start = () => {
    rescanHeader();
    document.addEventListener('eme:cart:open', rescanHeader);
    document.addEventListener('cart:refresh', rescanHeader);
    document.addEventListener('shopify:section:load', rescanHeader);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
